import React from 'react';

import Nav from '../../../components/specific/Navigation';
import { Toaster } from 'react-hot-toast';
import { TitlesTables } from '../../../components/global/Styles/titles';
import LoadingTables from '../../../components/global/LoadingTables';

const ServicesTable = ({ data, selectedMonth, selectedYear, handleMonthYearChange }) => {
  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Procesar datos para obtener información única y agrupada
  const processServicesData = (data) => {
    // Agrupar por SOLICITUD_ID para obtener solicitudes únicas
    const uniqueRequests = data.reduce((acc, item) => {
      const solicitudId = item.SOLICITUD_ID;
      if (!acc[solicitudId]) {
        acc[solicitudId] = {
          ...item,
          detalles: [],
        };
      }
      acc[solicitudId].detalles.push({
        SOLICITUD_DETALLE_ID: item.SOLICITUD_DETALLE_ID,
        SOLICITUD_DETALLE_TOTAL: item.SOLICITUD_DETALLE_TOTAL,
        TARIFA_NOMBRE: item.TARIFA_NOMBRE,
        TARIFA_GRUPO: item.TARIFA_GRUPO,
      });
      return acc;
    }, {});

    return Object.values(uniqueRequests);
  };

  const uniqueRequests = processServicesData(data);

  // Calcular estadísticas basadas en solicitudes únicas
  // Solo considerar estados "Ejecución" y "Finalizada" para estadísticas generales
  const relevantRequests = uniqueRequests.filter(
    (request) => request.SOLICITUD_ESTADO_DESCRIPCION === 'Ejecución' || request.SOLICITUD_ESTADO_DESCRIPCION === 'Finalizada'
  );

  const totalRequests = relevantRequests.length;
  const totalValue = relevantRequests.reduce((sum, request) => sum + (request.SOLICITUD_TOTAL || 0), 0);
  const uniqueClients = new Set(uniqueRequests.map((request) => request.CLIENTE_ID)).size;
  const uniqueVehicles = new Set(uniqueRequests.map((request) => request.VEHICULO_PLACA)).size;

  // Agrupar por cliente con estados y detalles de vehículos
  const clientStats = uniqueRequests.reduce((acc, request) => {
    const clientId = request.CLIENTE_ID;
    const estado = request.SOLICITUD_ESTADO_DESCRIPCION;
    const placa = request.VEHICULO_PLACA || 'Sin Vehículo';

    if (!acc[clientId]) {
      acc[clientId] = {
        name: request.CLIENTE_NOMBRE,
        estados: {},
        totalCount: 0,
        totalValue: 0,
        vehiculos: new Set(),
      };
    }

    // Inicializar estado si no existe
    if (!acc[clientId].estados[estado]) {
      acc[clientId].estados[estado] = {
        count: 0,
        value: 0,
        vehiculos: {},
      };
    }

    // Inicializar vehículo en el estado si no existe
    if (!acc[clientId].estados[estado].vehiculos[placa]) {
      acc[clientId].estados[estado].vehiculos[placa] = {
        count: 0,
        value: 0,
      };
    }

    // Contar y sumar valores
    acc[clientId].estados[estado].count++;
    acc[clientId].estados[estado].value += request.SOLICITUD_TOTAL || 0;
    acc[clientId].estados[estado].vehiculos[placa].count++;
    acc[clientId].estados[estado].vehiculos[placa].value += request.SOLICITUD_TOTAL || 0;

    acc[clientId].totalCount++;
    acc[clientId].totalValue += request.SOLICITUD_TOTAL || 0;
    if (request.VEHICULO_PLACA) {
      acc[clientId].vehiculos.add(request.VEHICULO_PLACA);
    }

    return acc;
  }, {});

  // Convertir Set de vehículos a número y calcular totales relevantes por cliente
  Object.values(clientStats).forEach((client) => {
    client.vehiculosCount = client.vehiculos.size;
    delete client.vehiculos;

    // Calcular total de Ejecución + Finalizada por cliente
    client.totalRelevantCount = 0;
    client.totalRelevantValue = 0;

    if (client.estados['Ejecución']) {
      client.totalRelevantCount += client.estados['Ejecución'].count;
      client.totalRelevantValue += client.estados['Ejecución'].value;
    }
    if (client.estados['Finalizada']) {
      client.totalRelevantCount += client.estados['Finalizada'].count;
      client.totalRelevantValue += client.estados['Finalizada'].value;
    }
  });

  // Agrupar por estado para estadísticas generales
  const statusStats = uniqueRequests.reduce((acc, request) => {
    const estado = request.SOLICITUD_ESTADO_DESCRIPCION;
    if (!acc[estado]) {
      acc[estado] = { count: 0, value: 0 };
    }
    acc[estado].count++;
    acc[estado].value += request.SOLICITUD_TOTAL || 0;
    return acc;
  }, {});

  // Estadísticas por placa (solo Ejecución y Finalizada)
  const vehicleStats = relevantRequests.reduce((acc, request) => {
    const placa = request.VEHICULO_PLACA || 'Sin Vehículo';
    const clientId = request.CLIENTE_ID;

    if (!acc[placa]) {
      acc[placa] = {
        placa: placa,
        servicios: 0,
        clientes: new Set(),
        valor: 0,
      };
    }

    acc[placa].servicios++;
    acc[placa].clientes.add(clientId);
    acc[placa].valor += request.SOLICITUD_TOTAL || 0;

    return acc;
  }, {});

  // Convertir Set de clientes a número
  Object.values(vehicleStats).forEach((vehicle) => {
    vehicle.clientesUnicos = vehicle.clientes.size;
    delete vehicle.clientes;
  });

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Reporte de Servicios</h2>
        <div className="flex gap-4">
          <select
            value={selectedMonth}
            onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
            className="rounded border px-3 py-1 text-sm">
            {months.map((month) => (
              <option
                key={month.value}
                value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => handleMonthYearChange(selectedMonth, parseInt(e.target.value))}
            className="rounded border px-3 py-1 text-sm">
            {years.map((year) => (
              <option
                key={year}
                value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Estadísticas generales */}
      <div className="mb-4 grid grid-cols-4 gap-4">
        <div className="rounded-lg bg-blue-50 p-4">
          <p className="text-sm text-blue-600">Total Solicitudes</p>
          <p className="text-2xl font-bold text-blue-800">{totalRequests}</p>
        </div>
        <div className="rounded-lg bg-green-50 p-4">
          <p className="text-sm text-green-600">Valor Total</p>
          <p className="text-2xl font-bold text-green-800">${totalValue.toLocaleString('es-CO')}</p>
        </div>
        <div className="rounded-lg bg-purple-50 p-4">
          <p className="text-sm text-purple-600">Clientes Únicos</p>
          <p className="text-2xl font-bold text-purple-800">{uniqueClients}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-orange-600 text-sm">Vehículos Únicos</p>
          <p className="text-orange-800 text-2xl font-bold">{uniqueVehicles}</p>
        </div>
      </div>

      {/* Nota explicativa */}
      <div className="mb-6 rounded-lg border-l-4 border-blue-400 bg-blue-50 p-3">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Nota:</strong> Las estadísticas de "Total Solicitudes" y "Valor Total" solo incluyen servicios en estado <strong>"Ejecución"</strong> y{' '}
              <strong>"Finalizada"</strong>. Los conteos de clientes y vehículos únicos incluyen todos los estados.
            </p>
          </div>
        </div>
      </div>

      {/* Estadísticas por estado */}
      <div className="mb-6">
        <h3 className="text-md mb-3 font-medium text-gray-900">Distribución por Estado</h3>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(statusStats).map(([estado, stats]) => (
            <div
              key={estado}
              className="rounded border p-3 text-center">
              <p className="truncate text-xs font-medium text-gray-600">{estado}</p>
              <p className="text-lg font-bold text-gray-900">{stats.count}</p>
              <p className="text-xs text-green-600">${stats.value.toLocaleString('es-CO')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Todos los clientes con estados detallados */}
      <div className="mb-6">
        <h3 className="text-md mb-3 font-medium text-gray-900">Clientes por Valor (Ejecución + Finalizada)</h3>
        <div className="grid grid-cols-1 gap-6">
          {Object.values(clientStats)
            .sort((a, b) => b.totalRelevantValue - a.totalRelevantValue)
            .map((client, index) => (
              <div
                key={index}
                className="rounded-lg border bg-white p-6 shadow-sm">
                {/* Header del cliente */}
                <div className="mb-4 flex items-center justify-between border-b pb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{client.name}</h4>
                    <div className="mt-1 flex gap-4 text-sm text-gray-600">
                      <span>
                        Total Solicitudes: <strong>{client.totalCount}</strong>
                      </span>
                      <span>
                        Vehículos: <strong>{client.vehiculosCount}</strong>
                      </span>
                      <span className="font-semibold text-green-600">Total: ${client.totalValue.toLocaleString('es-CO')}</span>
                    </div>
                    <div className="mt-2 rounded-lg border-l-4 border-green-400 bg-green-50 p-2">
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-700">
                          <strong>Ejecución + Finalizada:</strong> {client.totalRelevantCount} solicitudes
                        </span>
                        <span className="font-semibold text-green-800">${client.totalRelevantValue.toLocaleString('es-CO')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Estados con detalles */}
                <div className="md:grid-cols-2 lg:grid-cols-3 grid grid-cols-1 gap-4">
                  {Object.entries(client.estados).map(([estado, estadoData]) => (
                    <div
                      key={estado}
                      className="rounded-lg border bg-gray-50 p-4">
                      {/* Header del estado */}
                      <div className="mb-3 border-b border-gray-200 pb-2">
                        <h5 className="font-medium text-gray-900">{estado}</h5>
                        <div className="text-sm text-gray-600">
                          <span className="font-semibold">{estadoData.count}</span> solicitud{estadoData.count !== 1 ? 'es' : ''}
                          <span className="ml-2 font-semibold text-green-600">${estadoData.value.toLocaleString('es-CO')}</span>
                        </div>
                      </div>

                      {/* Vehículos por estado */}
                      <div className="space-y-2">
                        {Object.entries(estadoData.vehiculos)
                          .sort((a, b) => b[1].value - a[1].value)
                          .map(([placa, vehiculoData]) => (
                            <div
                              key={placa}
                              className="rounded border bg-white p-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span
                                  className="max-w-20 truncate font-medium text-gray-800"
                                  title={placa}>
                                  {placa}
                                </span>
                                <div className="text-right">
                                  <div className="text-gray-600">
                                    {vehiculoData.count} servicio{vehiculoData.count !== 1 ? 's' : ''}
                                  </div>
                                  <div className="font-semibold text-green-600">${vehiculoData.value.toLocaleString('es-CO')}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Estadísticas por placa (Ejecución + Finalizada) */}
      <div className="mb-6">
        <h3 className="text-md mb-3 font-medium text-gray-900">Estadísticas por Vehículo (Ejecución + Finalizada)</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Placa</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"># Servicios</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"># Clientes</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Valor Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Promedio por Servicio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {Object.values(vehicleStats)
                .sort((a, b) => b.valor - a.valor)
                .map((vehicle, index) => (
                  <tr
                    key={vehicle.placa}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{vehicle.placa}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {vehicle.servicios}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                        {vehicle.clientesUnicos}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-green-600">${vehicle.valor.toLocaleString('es-CO')}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      ${Math.round(vehicle.valor / vehicle.servicios).toLocaleString('es-CO')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Resumen de estadísticas por placa */}
        <div className="mt-4 grid grid-cols-4 gap-4">
          <div className="rounded-lg bg-blue-50 p-4 text-center">
            <p className="text-sm text-blue-600">Total Vehículos Activos</p>
            <p className="text-2xl font-bold text-blue-800">{Object.keys(vehicleStats).length}</p>
          </div>
          <div className="rounded-lg bg-green-50 p-4 text-center">
            <p className="text-sm text-green-600">Promedio Servicios/Vehículo</p>
            <p className="text-2xl font-bold text-green-800">
              {Object.keys(vehicleStats).length > 0
                ? Math.round(Object.values(vehicleStats).reduce((sum, v) => sum + v.servicios, 0) / Object.keys(vehicleStats).length)
                : 0}
            </p>
          </div>
          <div className="rounded-lg bg-purple-50 p-4 text-center">
            <p className="text-sm text-purple-600">Promedio Clientes/Vehículo</p>
            <p className="text-2xl font-bold text-purple-800">
              {Object.keys(vehicleStats).length > 0
                ? Math.round(Object.values(vehicleStats).reduce((sum, v) => sum + v.clientesUnicos, 0) / Object.keys(vehicleStats).length)
                : 0}
            </p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <p className="text-orange-600 text-sm">Promedio Valor/Vehículo</p>
            <p className="text-orange-800 text-2xl font-bold">
              $
              {Object.keys(vehicleStats).length > 0
                ? Math.round(Object.values(vehicleStats).reduce((sum, v) => sum + v.valor, 0) / Object.keys(vehicleStats).length).toLocaleString('es-CO')
                : 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewDashboard = (props) => {
  const { isLoading, servicesData, selectedMonth, selectedYear, handleMonthYearChange } = props.data;

  return (
    <div className="flex max-h-screen min-h-screen flex-col overflow-hidden ">
      <Nav />
      <Toaster
        toastOptions={{
          className: 'w-1/2',
        }}
      />
      <div className="flex flex-grow flex-col overflow-auto ">
        <div className="m-2 flex flex-grow flex-col   ">
          <div className="flex ">
            <TitlesTables>DASHBOARD</TitlesTables>
            <div className="flex-auto"></div>
          </div>

          {isLoading && <LoadingTables />}

          {/* Sección de Reporte de Servicios */}
          {!isLoading && (
            <ServicesTable
              data={servicesData}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              handleMonthYearChange={handleMonthYearChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewDashboard;
